import {
  getLivistheme,
  getLivisBrandColor,
  getLivisBrandTheme, // Add this import
  setLivisTheme,
  setLivisBrandColor,
  setLivisBrandTheme,
} from "./storage";

/**
 * Interface defining the structure of brand color configurations
 * Each brand color includes theme class names for both light and dark modes
 */
interface BrandColor {
  name: string; // Human-readable name for the brand color
  value: string; // Hex color value for the brand
  lightTheme: string; // CSS class name for light theme variant
  darkTheme: string; // CSS class name for dark theme variant
}

/**
 * Predefined brand color configurations
 * Contains all available theme combinations with their corresponding CSS classes
 * Each brand supports both light and dark theme variants
 */
export const brandColors: BrandColor[] = [
  {
    name: "Blue",
    value: "#0857de",
    lightTheme: "livis-light-theme",
    darkTheme: "livis-dark-theme",
  },
  {
    name: "Emerald",
    value: "#10b981",
    lightTheme: "livis-emerald-theme",
    darkTheme: "livis-emerald-dark-theme",
  },
  {
    name: "Purple",
    value: "#8b5cf6",
    lightTheme: "livis-purple-theme",
    darkTheme: "livis-purple-dark-theme",
  },
  {
    name: "Crimson",
    value: "#dc2626",
    lightTheme: "livis-crimson-theme",
    darkTheme: "livis-crimson-dark-theme",
  },
  {
    name: "Ocean",
    value: "#0891b2",
    lightTheme: "livis-ocean-theme",
    darkTheme: "livis-ocean-dark-theme",
  },
  {
    name: "Amber",
    value: "#f59e0b",
    lightTheme: "livis-amber-theme",
    darkTheme: "livis-amber-dark-theme",
  },
  {
    name: "Slate",
    value: "#475569",
    lightTheme: "livis-slate-theme",
    darkTheme: "livis-slate-dark-theme",
  },
];

/**
 * Applies the global theme to the entire application
 *
 * This function handles the complete theme application process including:
 * - Removing all existing theme classes to prevent conflicts
 * - Applying the correct theme class based on mode and brand color
 * - Setting data attributes for CSS specificity and debugging
 * - Persisting theme settings to local storage
 * - Setting up theme change monitoring
 *
 * @param themeMode - The theme mode ('light' or 'dark')
 * @param brandColor - The hex color value of the selected brand
 */
export const applyGlobalTheme = (
  themeMode: "light" | "dark",
  brandColor: string
): void => {
  // Find the brand configuration for the given color
  const selectedBrand = brandColors.find((brand) => brand.value === brandColor);

  // Error handling for invalid brand colors
  if (!selectedBrand) {
    console.error("❌ Brand not found for color:", brandColor);
    return;
  }

  /**
   * Complete list of all possible theme classes
   * Used to ensure clean slate before applying new theme
   * Includes base themes and all brand-specific variants
   */
  const allThemeClasses = [
    "livis-light-theme",
    "livis-dark-theme",
    "livis-emerald-theme",
    "livis-emerald-dark-theme",
    "livis-purple-theme",
    "livis-purple-dark-theme",
    "livis-crimson-theme",
    "livis-crimson-dark-theme",
    "livis-ocean-theme",
    "livis-ocean-dark-theme",
    "livis-amber-theme",
    "livis-amber-dark-theme",
    "livis-slate-theme",
    "livis-slate-dark-theme",
    "livis-rose-theme",
    "livis-rose-dark-theme",
    "livis-indigo-theme",
    "livis-indigo-dark-theme",
  ];

  // Remove all existing theme classes from both html and body elements
  allThemeClasses.forEach((className) => {
    document.documentElement.classList.remove(className);
    document.body.classList.remove(className);
  });

  // Determine the correct theme class based on mode and brand
  const themeClass =
    themeMode === "dark" ? selectedBrand.darkTheme : selectedBrand.lightTheme;

  // Apply the theme class to both html and body for maximum CSS reach
  document.documentElement.classList.add(themeClass);
  document.body.classList.add(themeClass);

  /**
   * Set data attributes for enhanced CSS targeting and debugging
   * These attributes provide additional CSS hooks and make theme debugging easier
   */
  document.documentElement.setAttribute("data-theme", themeMode);
  document.documentElement.setAttribute("data-brand-color", brandColor);
  document.documentElement.setAttribute("data-theme-class", themeClass);

  // Store current theme values for reference
  const currentMode = themeMode;
  const currentColor = brandColor;

  // Persist theme settings to local storage for session continuity
  setLivisTheme(themeMode);
  setLivisBrandColor(brandColor);
  setLivisBrandTheme(themeClass);

  // Set up monitoring to prevent accidental theme class removal
  watchForThemeChanges(themeClass);
};

/**
 * Monitors DOM changes to prevent accidental theme class removal
 *
 * Uses MutationObserver to watch for class attribute changes on html and body elements.
 * Automatically re-applies theme classes if they are removed by other scripts.
 * This ensures theme persistence even when third-party libraries modify DOM classes.
 *
 * @param expectedThemeClass - The theme class that should always be present
 */
const watchForThemeChanges = (expectedThemeClass: string): void => {
  // Clean up any existing observer to prevent memory leaks
  if (window.themeObserver) {
    window.themeObserver.disconnect();
  }

  // Create new MutationObserver to monitor class changes
  window.themeObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      // Only respond to class attribute changes
      if (
        mutation.type === "attributes" &&
        mutation.attributeName === "class"
      ) {
        const target = mutation.target as HTMLElement;

        // Re-apply theme class if it was removed
        if (!target.classList.contains(expectedThemeClass)) {
          target.classList.add(expectedThemeClass);
        }
      }
    });
  });

  // Monitor both body and documentElement for class changes
  window.themeObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ["class"], // Only watch class attribute changes
  });

  window.themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"], // Only watch class attribute changes
  });
};

/**
 * Global type declaration for the theme observer
 * Extends the Window interface to include our custom MutationObserver
 */
declare global {
  interface Window {
    themeObserver?: MutationObserver;
  }
}

/**
 * Changes the theme mode while preserving the current brand color
 *
 * This function allows switching between light and dark modes without
 * losing the user's selected brand color preference.
 *
 * @param mode - The new theme mode ('light' or 'dark')
 */
export const changeTheme = (mode: "light" | "dark"): void => {
  const currentColor = getLivisBrandColor(); // Preserve current brand color
  setLivisTheme(mode); // Update theme mode in storage
  applyGlobalTheme(mode, currentColor); // Apply the new theme

  // Update the stored theme class to match the new mode
  const selectedBrand = brandColors.find(
    (brand) => brand.value === currentColor
  );
  if (selectedBrand) {
    const themeClass =
      mode === "dark" ? selectedBrand.darkTheme : selectedBrand.lightTheme;
    setLivisBrandTheme(themeClass);
  }
};

/**
 * Changes the brand color while preserving the current theme mode
 *
 * This function allows switching between different brand colors without
 * losing the user's selected light/dark mode preference.
 *
 * @param color - The hex color value of the new brand color
 */
export const changeBrandColor = (color: string): void => {
  const selectedBrand = brandColors.find((brand) => brand.value === color);

  if (selectedBrand) {
    const currentMode = getLivistheme() as "light" | "dark"; // Preserve current mode
    setLivisBrandColor(color); // Update brand color in storage
    applyGlobalTheme(currentMode, color); // Apply the new brand theme

    // Update the stored theme class to match the new brand
    const themeClass =
      currentMode === "dark"
        ? selectedBrand.darkTheme
        : selectedBrand.lightTheme;
    setLivisBrandTheme(themeClass);
  }
};

/**
 * Initializes the theme system on application startup
 *
 * This function handles theme restoration from local storage with intelligent fallbacks:
 * 1. First tries to restore from saved brand theme class (most reliable)
 * 2. Falls back to separate theme mode and color values
 * 3. Provides sensible defaults if no saved preferences exist
 *
 * The brand theme approach is preferred because it captures the complete theme state
 * in a single value, reducing the chance of mismatched theme/color combinations.
 */
export const initializeTheme = (): void => {
  // Retrieve all saved theme preferences
  const savedTheme = getLivistheme() as "light" | "dark";
  const savedColor = getLivisBrandColor();
  const savedBrandTheme = getLivisBrandTheme(); // Most reliable source

  // Default fallback values
  let themeMode: "light" | "dark" = "light";
  let brandColor = "#0857de"; // Default blue brand color

  /**
   * Priority 1: Restore from saved brand theme class
   * This is the most reliable method as it captures the complete theme state
   */
  if (savedBrandTheme) {
    // Determine theme mode from the theme class name
    themeMode = savedBrandTheme.includes("-dark-") ? "dark" : "light";

    // Find the brand color that corresponds to this theme class
    const brandFromTheme = brandColors.find(
      (brand) =>
        brand.lightTheme === savedBrandTheme ||
        brand.darkTheme === savedBrandTheme
    );

    if (brandFromTheme) {
      brandColor = brandFromTheme.value;
    }
  } else if (savedColor) {
  /**
   * Priority 2: Restore from separate theme and color values
   * Fallback method for backward compatibility
   */
    themeMode = savedTheme || "light";
    brandColor = savedColor;
  }

  // Apply the determined or default theme
  applyGlobalTheme(themeMode, brandColor);
};
